import React, { useState, useEffect, useCallback } from 'react'
import { Routes, Route, useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import Header from './components/Header'
import ReportsList from './components/ReportsList'
import ReportDetail from './components/ReportDetail'

function App() {
  const [loading, setLoading] = useState(true)
  const [merchantId, setMerchantId] = useState(null)
  const [storeId, setStoreId] = useState(null)
  const [locationNr, setLocationNr] = useState(null)
  const [initError, setInitError] = useState(null)
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  // Parse URL parameters into state (called on mount and URL changes)
  const parseUrlParams = useCallback(() => {
    try {
      console.log("App: Parsing URL parameters");
      let midParam = null;
      let sidParam = null;
      let locParam = null;
      
      // First check if parameters are in the hash part (after #)
      const hashString = window.location.hash;
      if (hashString) {
        const questionMarkIndex = hashString.indexOf('?');
        if (questionMarkIndex !== -1) {
          const queryString = hashString.substring(questionMarkIndex);
          const hashParams = new URLSearchParams(queryString);
          
          midParam = hashParams.get('mid') || hashParams.get('merchant_id') || 
                     hashParams.get('merchantId') || hashParams.get('merchant-id');
                     
          sidParam = hashParams.get('sid') || hashParams.get('store_id') || 
                    hashParams.get('storeId') || hashParams.get('store-id');
                    
          locParam = hashParams.get('loc') || hashParams.get('location') || 
                    hashParams.get('locationNr') || hashParams.get('location-nr');
        }
      }
      
      // If not found in hash, check URL search params (after ?)
      if (!midParam) {
        midParam = searchParams.get('mid') || searchParams.get('merchant_id') || 
                   searchParams.get('merchantId') || searchParams.get('merchant-id');
      }
      
      if (!sidParam) {
        sidParam = searchParams.get('sid') || searchParams.get('store_id') || 
                   searchParams.get('storeId') || searchParams.get('store-id');
      }
      
      if (!locParam) {
        locParam = searchParams.get('loc') || searchParams.get('location') || 
                   searchParams.get('locationNr') || searchParams.get('location-nr');
      }
      
      // Last resort: try to extract from full URL string using regex
      if (!midParam) {
        const url = window.location.href;
        const midMatch = url.match(/[?&#](mid|merchant_id|merchantId|merchant-id)=([^&#]*)/i);
        if (midMatch && midMatch[2]) {
          midParam = decodeURIComponent(midMatch[2]);
        }
      }

      // Update state with found parameters
      const newMerchantId = midParam || null;
      const newStoreId = sidParam || null;
      const newLocationNr = locParam || '*';
      
      console.log("Parsed parameters:", { newMerchantId, newStoreId, newLocationNr });
      
      // Check if we need to update the state
      if (merchantId !== newMerchantId || storeId !== newStoreId || locationNr !== newLocationNr) {
        setMerchantId(newMerchantId);
        setStoreId(newStoreId);
        setLocationNr(newLocationNr);
      }
      
      setInitError(null);
      setLoading(false);

      // Handle parameter duplication
      // If we have parameters in both URL and hash, normalize to hash only
      const urlParams = new URLSearchParams(window.location.search);
      const hasUrlParams = urlParams.has('mid') || urlParams.has('loc') || urlParams.has('sid');
      const hasHashParams = hashString && hashString.includes('?');
      
      // If we have parameters in both places, normalize to hash only
      if (hasUrlParams && hasHashParams && newMerchantId) {
        console.log("Detected duplicate parameters in URL and hash, normalizing...");
        
        // Build new hash params
        const normalizedParams = new URLSearchParams();
        
        if (newMerchantId) normalizedParams.set('mid', newMerchantId);
        if (newStoreId) normalizedParams.set('sid', newStoreId);
        if (newLocationNr && newLocationNr !== '*') normalizedParams.set('loc', newLocationNr);
        
        const searchString = normalizedParams.toString();
        
        // Navigate with empty search and all params in hash
        navigate({
          pathname: '/',
          search: '', // Clear URL search params
          hash: searchString ? `?${searchString}` : ''
        }, { replace: true });
      }
      // If we only have URL params but no hash params, move them to hash
      else if (hasUrlParams && newMerchantId && !hasHashParams) {
        console.log("Moving URL parameters to hash...");
        
        // Build new hash params
        const normalizedParams = new URLSearchParams();
        
        if (newMerchantId) normalizedParams.set('mid', newMerchantId);
        if (newStoreId) normalizedParams.set('sid', newStoreId);
        if (newLocationNr && newLocationNr !== '*') normalizedParams.set('loc', newLocationNr);
        
        const searchString = normalizedParams.toString();
        
        // Navigate with empty search and all params in hash
        navigate({
          pathname: '/',
          search: '', // Clear URL search params
          hash: searchString ? `?${searchString}` : ''
        }, { replace: true });
      }
      
    } catch (error) {
      console.error("Error parsing URL parameters:", error);
      setInitError("Failed to parse URL parameters. Please check the URL and try again.");
      setLoading(false);
    }
  }, [searchParams, location, merchantId, storeId, locationNr, navigate]);

  // Parse URL parameters when component mounts or location changes
  useEffect(() => {
    parseUrlParams();
  }, [parseUrlParams, location]);

  // Listen for hash changes
  useEffect(() => {
    const handleHashChange = () => {
      console.log("Hash changed, re-parsing parameters");
      parseUrlParams();
    };
    
    window.addEventListener('hashchange', handleHashChange);
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [parseUrlParams]);

  // Listen for custom location change event
  useEffect(() => {
    const handleLocationChange = (event) => {
      setLocationNr(event.detail.locationNr || '*');
    };

    window.addEventListener('locationChanged', handleLocationChange);
    return () => {
      window.removeEventListener('locationChanged', handleLocationChange);
    };
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (initError) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50 p-4">
        <div className="bg-red-50 border border-red-200 text-red-700 p-6 rounded-lg max-w-md">
          <h2 className="text-xl font-bold mb-4">Error Initializing App</h2>
          <p className="mb-4">{initError}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded"
          >
            Refresh Page
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header
        merchantId={merchantId}
        locationNr={locationNr}
        onLocationChange={(newLocation) => setLocationNr(newLocation)}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {location.pathname === '/' && (
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-900">Return Reports</h1>
          </div>
        )}
        <Routes>
          <Route
            path="/"
            element={<ReportsList merchantId={merchantId} storeId={storeId} locationNr={locationNr} />}
          />
          <Route path="/report/:orderId" element={<ReportDetail merchantId={merchantId} />} />
        </Routes>
      </div>
    </div>
  );
}

export default App;